import { useEffect, useState } from 'react';

import type { Entity, Entry } from '../api/types.ts';
import { useT } from '../i18n/index.ts';
import { entryDayLabel, karachiDate, nearDayKey } from '../lib/dates.ts';
import { rs } from '../lib/money.ts';
import { EntityIcon, Icon } from '../ui/icons.tsx';
import { Num, StatusChip } from '../ui/parts.tsx';

export const UNDO_SECONDS = 10;

/**
 * After Save: a tick, then the entry as a paper receipt (company, date, item, amount, receipt,
 * status) with the company's balance now, and the next actions. Fits one screen on every phone
 * (compact on short ones), a centred card on tablets and desktop, side by side when sideways.
 */
export function Saved(props: {
  entry: Entry;
  entity: Entity;
  balance: number;
  canUndo: boolean;
  /** Another company / house paid the shop (Dues). */
  payerName?: string | null;
  onAnother: () => void;
  onUndo: () => void;
  onDone: () => void;
}) {
  const { t, lang } = useT();
  const [left, setLeft] = useState(UNDO_SECONDS);
  useEffect(() => {
    const id = window.setInterval(() => setLeft((n) => Math.max(0, n - 1)), 1000);
    return () => window.clearInterval(id);
  }, []);
  const e = props.entry;
  const queued = e.syncState !== 'synced';
  const near = nearDayKey(e.txnDate, karachiDate());
  const day = near ? t(near) : entryDayLabel(e.txnDate, karachiDate(), lang);
  const receipt =
    e.receiptCount > 0
      ? t('add.photos', { count: e.receiptCount })
      : e.noReceiptReason
        ? `${t('add.noReceiptShort')} · ${e.noReceiptReason}`
        : '—';
  const showUndo = props.canUndo && left > 0;

  return (
    <div className="safe-top flex min-h-app flex-col bg-ground split:items-center split:p-6 short:p-3">
      <div className="flex w-full flex-1 flex-col split:my-auto split:max-w-md split:flex-none short:max-w-3xl short:flex-row short:items-center short:gap-4">
        <main
          className="flex flex-1 flex-col items-center justify-center gap-3 px-4 pt-5 pb-2 low:gap-2 low:pt-3 short:gap-2 short:p-0"
          role="status"
        >
          <span className="flex size-[clamp(56px,16vw,72px)] items-center justify-center rounded-full bg-brand text-white shadow-[0_8px_24px_rgba(15,110,106,0.35)] motion-safe:animate-[saved-pop_0.4s_ease-out] tight:size-12 short:size-12">
            <Icon name="check" size={36} stroke={3} className="tight:size-7 short:size-7" />
          </span>
          <div className="flex flex-col items-center gap-1">
            <h1 className="m-0 text-[clamp(22px,6.5vw,28px)] leading-tight font-extrabold tracking-tight">{t('saved.title')}</h1>
            <span className={`inline-flex items-center gap-1.5 text-fluid-sm font-semibold ${queued ? 'text-ink' : 'text-success-text'}`}>
              <Icon name={queued ? 'cloudOff' : 'check'} size={15} stroke={2.5} />
              {queued ? t('saved.queued') : t('saved.sent')}
            </span>
          </div>

          {/* The entry as a paper receipt (same look as the sign-in card). */}
          <section className="receipt-edge mb-2.5 w-full max-w-md overflow-visible bg-paper shadow-[0_1px_2px_rgba(19,33,31,0.06)]">
            <div className="h-1.5" style={{ background: props.entity.colour }} />
            <div className="flex flex-col px-4 pt-3 pb-3.5 low:pt-2.5 low:pb-3">
              <div className="flex items-center gap-2.5">
                <span
                  className="flex size-9 shrink-0 items-center justify-center rounded-lg text-white tight:size-8"
                  style={{ background: props.entity.colour }}
                >
                  <EntityIcon entity={props.entity} size={18} />
                </span>
                <span className="min-w-0 flex-1 truncate text-fluid-base font-bold">{props.entity.name}</span>
                <span className="inline-flex shrink-0 items-center gap-1 text-fluid-sm font-semibold text-muted">
                  <Icon name="calendar" size={14} />
                  {day}
                </span>
              </div>

              <hr className="rule-dashed my-3 border-0 low:my-2.5 short:my-1.5" />

              <div className="flex items-baseline justify-between gap-3">
                <span className="min-w-0 truncate text-fluid-lg font-bold rtl:text-[clamp(16px,4.8vw,19px)]">{e.itemName}</span>
                {e.quantity ? (
                  <span className="shrink-0 text-fluid-sm font-semibold text-muted">
                    <Num>{e.quantity}</Num> {t(`units.${e.unit ?? 'pcs'}`)}
                  </span>
                ) : null}
              </div>
              <div className="mt-1 flex items-baseline justify-between gap-3">
                <span className="text-fluid-sm font-semibold text-muted">{t('saved.spent')}</span>
                <Num className="text-[clamp(26px,9vw,36px)] leading-tight font-extrabold tracking-tight tight:text-[26px]">
                  {rs(e.amount)}
                </Num>
              </div>

              <hr className="rule-dashed my-3 border-0 low:my-2.5 short:my-1.5" />

              <dl className="m-0 grid short:hidden grid-cols-[auto_1fr] items-center gap-x-3 gap-y-2 text-fluid-sm">
                <dt className="font-semibold text-muted">{t('saved.receiptLabel')}</dt>
                <dd className="m-0 flex min-w-0 items-center justify-end gap-1.5 text-end font-semibold">
                  <Icon
                    name={e.receiptCount > 0 ? 'camera' : 'noReceipt'}
                    size={15}
                    className={e.receiptCount > 0 ? '' : 'text-warning-text'}
                  />
                  <span className="truncate">{receipt}</span>
                </dd>
                {props.payerName && (
                  <>
                    <dt className="font-semibold text-muted">{t('add.whoPaid')}</dt>
                    <dd className="m-0 truncate text-end font-semibold">{props.payerName}</dd>
                  </>
                )}
                <dt className="font-semibold text-muted">{t('saved.statusLabel')}</dt>
                <dd className="m-0 flex justify-end">
                  <StatusChip entry={e} />
                </dd>
              </dl>

              <div
                className="mt-3 flex items-center justify-between gap-3 rounded-xl px-3.5 py-2.5 text-white low:mt-2.5 low:py-2 short:mt-1.5 short:py-1.5"
                style={{ background: props.entity.colour }}
              >
                <span className="min-w-0 text-fluid-sm leading-snug font-semibold">
                  {t('saved.balanceNow', { entity: props.entity.name })}
                </span>
                <Num className="shrink-0 text-[clamp(20px,6.5vw,26px)] leading-tight font-extrabold tracking-tight">
                  {rs(props.balance)}
                </Num>
              </div>
            </div>
          </section>
        </main>

        <footer className="safe-bottom flex w-full max-w-md shrink-0 flex-col gap-2.5 self-center px-4 pt-2 pb-4 low:gap-2 low:pb-3 short:w-80 short:p-0">
          <button
            type="button"
            onClick={props.onAnother}
            className="flex h-14 items-center justify-center gap-2 rounded-xl bg-brand px-3 text-fluid-lg font-bold text-white hover:bg-brand-dark low:h-12 short:h-12 rtl:text-[clamp(16px,4.8vw,19px)]"
          >
            <Icon name="plus" size={22} stroke={2.75} />
            <span className="truncate">{t('saved.another', { entity: props.entity.name })}</span>
          </button>
          <div className="flex gap-2.5 low:gap-2">
            {showUndo && (
              <button
                type="button"
                onClick={props.onUndo}
                className="relative flex h-12 flex-1 items-center justify-center gap-2 overflow-hidden rounded-xl border-[1.5px] border-line-strong bg-paper text-fluid-base font-bold hover:bg-ground short:h-11"
              >
                {/* How much of the undo window is left. */}
                <span
                  aria-hidden="true"
                  className="absolute inset-y-0 start-0 bg-brand-tint transition-[width] duration-1000 ease-linear"
                  style={{ width: `${(left / UNDO_SECONDS) * 100}%` }}
                />
                <span className="relative flex items-center gap-2">
                  <Icon name="undo" size={18} flip />
                  {t('saved.undo', { seconds: left })}
                </span>
              </button>
            )}
            <button
              type="button"
              onClick={props.onDone}
              className="h-12 flex-1 rounded-xl border-[1.5px] border-line-strong bg-paper text-fluid-base font-bold hover:bg-ground short:h-11"
            >
              {t('saved.done')}
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
}
